import React, { useState, useEffect } from "react";
import {
  Calendar,
  User,
  Edit,
  Trash2,
  Eye,
  Heart,
  Share2,
  Clock,
  ArrowUp,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useIsMobile } from "../../App";

const BlogSystem = () => {
  // Use the passed theme hook or fallback
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [currentView, setCurrentView] = useState("blog"); // 'blog', 'admin', 'article'
  const [articles, setArticles] = useState([
    {
      id: 1,
      title: "Як розпізнати ранні симптоми серцевих захворювань",
      slug: "ranni-simptomi-sercevikh-zakhvoryuvan",
      excerpt:
        "Раннє виявлення проблем із серцем може врятувати життя. Дізнайтеся про основні попереджувальні сигнали...",
      content: `# Як розпізнати ранні симптоми серцевих захворювань

Серцево-судинні захворювання залишаються однією з основних причин смертності у світі. Раннє виявлення симптомів може значно покращити прогноз та якість життя.

## Основні симптоми

### 1. Біль у грудях
Найпоширеніший симптом серцевих проблем. Може проявлятися як:
- Стискаючий біль
- Відчуття тиску
- Печія-подібні відчуття

### 2. Задишка
Утруднене дихання при:
- Фізичному навантаженні
- У спокої
- В положенні лежачи

### 3. Втома
Незвичайна втома може вказувати на:
- Погіршення роботи серця
- Зниження серцевого викиду

## Коли звертатися до лікаря

Якщо ви відчуваєте будь-які з цих симптомів, особливо в поєднанні, негайно зверніться до кардіолога.`,
      category: "symptoms",
      author: "Д-р Олена Петренко",
      date: "2024-06-15",
      views: 1250,
      likes: 89,
      comments: [
        {
          id: 1,
          author: "Марія К.",
          content: "Дуже корисна стаття! Тепер знаю на що звертати увагу.",
          date: "2024-06-16",
        },
        {
          id: 2,
          author: "Андрій М.",
          content: "Чи можете порекомендувати хорошого кардіолога у Києві?",
          date: "2024-06-17",
        },
      ],
      tags: ["серце", "симптоми", "діагностика"],
      featured: true,
      published: true,
    },
    {
      id: 2,
      title: "10 продуктів для здорового серця",
      slug: "10-produktiv-dlya-zdorovogo-sercya",
      excerpt:
        "Правильне харчування - основа здоров'я серця. Ознайомтеся з топ-10 продуктами, які підтримають ваше серце...",
      content: `# 10 продуктів для здорового серця

Правильне харчування відіграє ключову роль у підтримці здоров'я серцево-судинної системи.

## Топ-10 продуктів

1. **Жирна риба** - омега-3 жирні кислоти
2. **Горіхи** - корисні жири та білки
3. **Авокадо** - мононенасичені жири
4. **Ягоди** - антиоксиданти
5. **Листова зелень** - фолієва кислота
6. **Цільнозернові** - клітковина
7. **Оливкова олія** - поліфеноли
8. **Томати** - лікопін
9. **Темний шоколад** - флавоноїди
10. **Зелений чай** - катехіни`,
      category: "nutrition",
      author: "Анна Коваленко",
      date: "2024-06-10",
      views: 980,
      likes: 156,
      comments: [],
      tags: ["харчування", "профілактика", "здоровий спосіб життя"],
      featured: false,
      published: true,
    },
    {
      id: 3,
      title: "Стрес і серце: як захистити себе",
      slug: "stres-i-serce-yak-zakhystyt-sebe",
      excerpt:
        "Хронічний стрес негативно впливає на серце. Дізнайтеся, як ефективно управляти стресом...",
      content:
        "# Стрес і серце: як захистити себе\n\nХронічний стрес є одним із найбільших ворогів серцево-судинної системи...",
      category: "lifestyle",
      author: "Д-р Ігор Мельник",
      date: "2024-06-05",
      views: 750,
      likes: 67,
      comments: [],
      tags: ["стрес", "профілактика", "психологія"],
      featured: false,
      published: true,
    },
  ]);

  const [categories] = useState([
    { id: "symptoms", name: "Поширені симптоми", count: 1 },
    { id: "nutrition", name: "Здорове харчування", count: 1 },
    { id: "lifestyle", name: "Спосіб життя", count: 1 },
    { id: "treatment", name: "Лікування", count: 0 },
    { id: "prevention", name: "Профілактика", count: 0 },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentArticle, setCurrentArticle] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");

  // Admin states
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    content: "",
    excerpt: "",
    category: "symptoms",
    tags: "",
    featured: false,
  });

  const [showScrollTop, setShowScrollTop] = useState(false);

  const mainContainerStyle = {
    minHeight: "100vh",
    backgroundColor: theme.background,
    color: theme.text,
    fontFamily: "Inter, Arial, sans-serif",
    position: "relative",
    overflowY: "auto",
    height: "100%",
    padding: isMobile ? "8px 0" : "24px 0",
    boxSizing: "border-box",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView, currentArticle]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const filteredArticles = articles
    .filter((article) => {
      const matchesSearch =
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      const matchesCategory = article.category === "symptoms";
      const isPublished = article.published;

      return matchesSearch && matchesCategory && isPublished;
    })
    .sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .trim();
  };

  const handleArticleSubmit = (e) => {
    e.preventDefault();

    const newArticle = {
      id: editingArticle ? editingArticle.id : Date.now(),
      ...articleForm,
      slug: generateSlug(articleForm.title),
      author: "Адміністратор",
      date: editingArticle
        ? editingArticle.date
        : new Date().toISOString().split("T")[0],
      views: editingArticle ? editingArticle.views : 0,
      likes: editingArticle ? editingArticle.likes : 0,
      comments: editingArticle ? editingArticle.comments : [],
      tags: articleForm.tags.split(",").map((tag) => tag.trim()),
      published: true,
    };

    if (editingArticle) {
      setArticles(
        articles.map((article) =>
          article.id === editingArticle.id ? newArticle : article,
        ),
      );
    } else {
      setArticles([...articles, newArticle]);
    }

    setArticleForm({
      title: "",
      content: "",
      excerpt: "",
      category: "symptoms",
      tags: "",
      featured: false,
    });
    setEditingArticle(null);
  };

  const handleDeleteArticle = (id) => {
    if (window.confirm("Ви впевнені, що хочете видалити цю статтю?")) {
      setArticles(articles.filter((article) => article.id !== id));
    }
  };

  const handleLikeArticle = (id) => {
    setArticles(
      articles.map((article) =>
        article.id === id ? { ...article, likes: article.likes + 1 } : article,
      ),
    );
    if (currentArticle && currentArticle.id === id) {
      setCurrentArticle({ ...currentArticle, likes: currentArticle.likes + 1 });
    }
  };

  const handleAddComment = (articleId) => {
    if (!newComment.trim()) return;

    const comment = {
      id: Date.now(),
      author: "Гість",
      content: newComment,
      date: new Date().toISOString().split("T")[0],
    };

    setArticles(
      articles.map((article) =>
        article.id === articleId
          ? { ...article, comments: [...article.comments, comment] }
          : article,
      ),
    );

    if (currentArticle && currentArticle.id === articleId) {
      setCurrentArticle({
        ...currentArticle,
        comments: [...currentArticle.comments, comment],
      });
    }

    setNewComment("");
  };

  const ArticleCard = ({ article }) => (
    <div
      style={{
        backgroundColor: theme.cardBackground,
        borderRadius: "10px",
        border: `1px solid ${theme.border}`,
        margin: "0 auto",
        maxWidth: "500px",
        padding: "32px 24px",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        cursor: "pointer",
        transition: "border 0.2s",
      }}
      onClick={() => {
        setCurrentArticle(article);
        setCurrentView("article");
        setArticles(
          articles.map((a) =>
            a.id === article.id ? { ...a, views: a.views + 1 } : a,
          ),
        );
      }}
    >
      <h3
        style={{
          fontSize: "22px",
          fontWeight: 600,
          margin: 0,
          color: theme.text,
          textAlign: "center",
        }}
      >
        {article.title}
      </h3>
      <p
        style={{
          color: theme.textSecondary,
          fontSize: "16px",
          textAlign: "center",
          margin: 0,
        }}
      >
        {article.excerpt}
      </p>
      <div
        style={{
          color: theme.textTertiary,
          fontSize: "14px",
          marginTop: "8px",
        }}
      >
        {new Date(article.date).toLocaleDateString("uk-UA")} • {article.author}
      </div>
    </div>
  );

  const ArticleView = ({ article }) => (
    <div style={{ maxWidth: "768px", margin: "0 auto", padding: "0 16px" }}>
      <button
        onClick={() => setCurrentView("blog")}
        style={{
          marginBottom: "24px",
          color: theme.primary,
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
        }}
      >
        ← Повернутися до блогу
      </button>

      <article
        style={{
          backgroundColor: theme.cardBackground,
          borderRadius: "8px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "32px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "24px",
            }}
          >
            <span
              style={{
                padding: "8px 16px",
                backgroundColor: theme.tagBackground,
                color: theme.tagText,
                borderRadius: "9999px",
                fontWeight: "500",
              }}
            >
              {categories.find((cat) => cat.id === article.category)?.name}
            </span>
            {article.featured && (
              <span
                style={{
                  padding: "4px 12px",
                  backgroundColor: theme.featuredBackground,
                  color: theme.featuredText,
                  borderRadius: "9999px",
                  fontSize: "14px",
                }}
              >
                Рекомендована
              </span>
            )}
          </div>

          <h1
            style={{
              fontSize: "36px",
              fontWeight: "bold",
              color: theme.text,
              marginBottom: "16px",
              lineHeight: "1.2",
            }}
          >
            {article.title}
          </h1>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "32px",
              paddingBottom: "24px",
              borderBottom: `1px solid ${theme.border}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "24px",
                color: theme.textSecondary,
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <User size={20} />
                {article.author}
              </span>
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Calendar size={20} />
                {new Date(article.date).toLocaleDateString("uk-UA")}
              </span>
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Clock size={20} />~{Math.ceil(article.content.length / 1000)}{" "}
                хв читання
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: theme.textSecondary,
                }}
              >
                <Eye size={20} />
                {article.views}
              </span>
              <button
                onClick={() => handleLikeArticle(article.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: theme.likeColor,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Heart size={20} />
                {article.likes}
              </button>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: theme.primary,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Share2 size={20} />
                Поділитися
              </button>
            </div>
          </div>

          <div
            style={{
              fontSize: "18px",
              lineHeight: "1.8",
              marginBottom: "32px",
              color: theme.text,
            }}
          >
            {article.content.split("\n").map((paragraph, index) => {
              if (paragraph.startsWith("# ")) {
                return (
                  <h1
                    key={index}
                    style={{
                      fontSize: "28px",
                      fontWeight: "bold",
                      marginTop: "32px",
                      marginBottom: "16px",
                      color: theme.text,
                    }}
                  >
                    {paragraph.slice(2)}
                  </h1>
                );
              } else if (paragraph.startsWith("## ")) {
                return (
                  <h2
                    key={index}
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      marginTop: "24px",
                      marginBottom: "12px",
                      color: theme.text,
                    }}
                  >
                    {paragraph.slice(3)}
                  </h2>
                );
              } else if (paragraph.startsWith("### ")) {
                return (
                  <h3
                    key={index}
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      marginTop: "16px",
                      marginBottom: "8px",
                      color: theme.text,
                    }}
                  >
                    {paragraph.slice(4)}
                  </h3>
                );
              } else if (paragraph.startsWith("- ")) {
                return (
                  <li
                    key={index}
                    style={{
                      marginLeft: "16px",
                      marginBottom: "4px",
                      color: theme.text,
                    }}
                  >
                    {paragraph.slice(2)}
                  </li>
                );
              } else if (paragraph.trim()) {
                return (
                  <p
                    key={index}
                    style={{ marginBottom: "16px", color: theme.text }}
                  >
                    {paragraph}
                  </p>
                );
              }
              return null;
            })}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "32px",
            }}
          >
            {article.tags.map((tag) => (
              <span
                key={tag}
                style={{
                  padding: "6px 12px",
                  backgroundColor: theme.tagBackground,
                  color: theme.tagText,
                  borderRadius: "9999px",
                  fontSize: "14px",
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>

        {/* Comments Section */}
        <div
          style={{
            borderTop: `1px solid ${theme.border}`,
            backgroundColor: theme.commentsBackground,
            padding: "32px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: theme.text,
              }}
            >
              Коментарі ({article.comments.length})
            </h3>
            <button
              onClick={() => setShowComments(!showComments)}
              style={{
                color: theme.primary,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {showComments ? "Приховати" : "Показати"} коментарі
            </button>
          </div>

          {showComments && (
            <div>
              <div style={{ marginBottom: "24px" }}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Напишіть свій коментар..."
                  style={{
                    width: "100%",
                    padding: "16px",
                    border: `1px solid ${theme.border}`,
                    borderRadius: "8px",
                    resize: "none",
                    fontSize: "16px",
                    fontFamily: "inherit",
                    backgroundColor: theme.inputBackground,
                    color: theme.text,
                  }}
                  rows="4"
                />
                <button
                  onClick={() => handleAddComment(article.id)}
                  style={{
                    marginTop: "12px",
                    padding: "8px 24px",
                    backgroundColor: theme.primary,
                    color: theme.buttonText,
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  Додати коментар
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {article.comments.map((comment) => (
                  <div
                    key={comment.id}
                    style={{
                      backgroundColor: theme.cardBackground,
                      padding: "16px",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontWeight: "600", color: theme.text }}>
                        {comment.author}
                      </span>
                      <span
                        style={{ fontSize: "14px", color: theme.textSecondary }}
                      >
                        {new Date(comment.date).toLocaleDateString("uk-UA")}
                      </span>
                    </div>
                    <p style={{ color: theme.text, margin: 0 }}>
                      {comment.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );

  const AdminPanel = () => (
    <div style={{ maxWidth: "1152px", margin: "0 auto", padding: "0 16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "32px",
        }}
      >
        <h1 style={{ fontSize: "28px", fontWeight: "bold", color: "#111827" }}>
          Панель адміністратора
        </h1>
        <button
          onClick={() => setCurrentView("blog")}
          style={{
            padding: "8px 16px",
            color: "#3b82f6",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Повернутися до блогу
        </button>
      </div>

      {/* Article Form */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          padding: "24px",
          marginBottom: "32px",
        }}
      >
        <h2
          style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "24px" }}
        >
          {editingArticle ? "Редагувати статтю" : "Додати нову статтю"}
        </h2>

        <form
          onSubmit={handleArticleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "24px" }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              Заголовок
            </label>
            <input
              type="text"
              value={articleForm.title}
              onChange={(e) =>
                setArticleForm({ ...articleForm, title: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "16px",
              }}
              required
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              Короткий опис
            </label>
            <textarea
              value={articleForm.excerpt}
              onChange={(e) =>
                setArticleForm({ ...articleForm, excerpt: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                resize: "none",
                fontSize: "16px",
              }}
              rows="3"
              required
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  marginBottom: "8px",
                }}
              >
                Категорія
              </label>
              <select
                value={articleForm.category}
                onChange={(e) =>
                  setArticleForm({ ...articleForm, category: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "16px",
                }}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                  marginBottom: "8px",
                }}
              >
                Теги (через кому)
              </label>
              <input
                type="text"
                value={articleForm.tags}
                onChange={(e) =>
                  setArticleForm({ ...articleForm, tags: e.target.value })
                }
                placeholder="тег1, тег2, тег3"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "16px",
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: "flex", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={articleForm.featured}
                onChange={(e) =>
                  setArticleForm({ ...articleForm, featured: e.target.checked })
                }
                style={{ marginRight: "8px" }}
              />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#374151",
                }}
              >
                Рекомендована стаття
              </span>
            </label>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              Контент (Markdown)
            </label>
            <textarea
              value={articleForm.content}
              onChange={(e) =>
                setArticleForm({ ...articleForm, content: e.target.value })
              }
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                resize: "none",
                fontFamily: "monospace",
                fontSize: "14px",
              }}
              rows="15"
              placeholder="# Заголовок&#10;&#10;Ваш контент тут..."
              required
            />
          </div>

          <div style={{ display: "flex", gap: "16px" }}>
            <button
              type="submit"
              style={{
                padding: "12px 24px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              {editingArticle ? "Оновити" : "Опублікувати"}
            </button>
            {editingArticle && (
              <button
                type="button"
                onClick={() => {
                  setEditingArticle(null);
                  setArticleForm({
                    title: "",
                    content: "",
                    excerpt: "",
                    category: "symptoms",
                    tags: "",
                    featured: false,
                  });
                }}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                Скасувати
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Articles Management */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <h3
            style={{ fontSize: "20px", fontWeight: "bold", color: "#111827" }}
          >
            Управління статтями
          </h3>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%" }}>
            <thead style={{ backgroundColor: "#f9fafb" }}>
              <tr>
                <th
                  style={{
                    padding: "12px 24px",
                    textAlign: "left",
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Стаття
                </th>
                <th
                  style={{
                    padding: "12px 24px",
                    textAlign: "left",
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Категорія
                </th>
                <th
                  style={{
                    padding: "12px 24px",
                    textAlign: "left",
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Статистика
                </th>
                <th
                  style={{
                    padding: "12px 24px",
                    textAlign: "left",
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Дії
                </th>
              </tr>
            </thead>
            <tbody
              style={{
                backgroundColor: "#ffffff",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              {articles.map((article) => (
                <tr
                  key={article.id}
                  style={{ borderBottom: "1px solid #e5e7eb" }}
                >
                  <td style={{ padding: "16px 24px" }}>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#111827",
                        }}
                      >
                        {article.title}
                      </div>
                      <div style={{ fontSize: "14px", color: "#6b7280" }}>
                        {article.date}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "16px 24px" }}>
                    <span
                      style={{
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: "600",
                        borderRadius: "9999px",
                        backgroundColor: "#dbeafe",
                        color: "#1e40af",
                      }}
                    >
                      {
                        categories.find((cat) => cat.id === article.category)
                          ?.name
                      }
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "16px 24px",
                      fontSize: "14px",
                      color: "#6b7280",
                    }}
                  >
                    <div>Перегляди: {article.views}</div>
                    <div>Лайки: {article.likes}</div>
                    <div>Коментарі: {article.comments.length}</div>
                  </td>
                  <td
                    style={{
                      padding: "16px 24px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => {
                          setEditingArticle(article);
                          setArticleForm({
                            title: article.title,
                            content: article.content,
                            excerpt: article.excerpt,
                            category: article.category,
                            tags: article.tags.join(", "),
                            featured: article.featured,
                          });
                        }}
                        style={{
                          color: "#3b82f6",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteArticle(article.id)}
                        style={{
                          color: "#dc2626",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const BlogMain = () => (
    <div
      style={{
        maxWidth: "600px",
        margin: "0 auto",
        padding: "0 8px",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", margin: "48px 0 32px 0" }}>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: 700,
            color: "#222",
            margin: 0,
            letterSpacing: "-1px",
          }}
        >
          CardioHealth Blog
        </h1>
        <p style={{ fontSize: "18px", color: "#666", margin: "16px 0 0 0" }}>
          Корисні поради та експертні статті для здоров'я серця
        </p>
      </div>
      {/* Search */}
      <div
        style={{
          margin: "0 0 32px 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <input
          type="text"
          placeholder="Пошук статей..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            maxWidth: "350px",
            padding: "12px 16px",
            border: `1px solid ${theme.border}`,
            borderRadius: "8px",
            fontSize: "16px",
            outline: "none",
            backgroundColor: theme.inputBackground,
            color: theme.text,
          }}
        />
      </div>
      {/* Articles */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "32px",
          alignItems: "center",
        }}
      >
        {filteredArticles.length > 0 ? (
          filteredArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))
        ) : (
          <div
            style={{
              textAlign: "center",
              color: "#aaa",
              fontSize: "18px",
              margin: "48px 0",
            }}
          >
            Статті не знайдено
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={mainContainerStyle}>
      {/* Navigation */}
      <nav
        style={{
          background: theme.navBackground,
          borderBottom: `1px solid ${theme.border}`,
          padding: "0",
        }}
      >
        <div style={{ maxWidth: "600px", margin: "0 auto", padding: "0 8px" }}>
          <div
            style={{
              height: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontSize: "22px",
                fontWeight: 700,
                color: theme.primary,
                letterSpacing: "-1px",
              }}
            >
              CardioHealth Blog
            </span>
          </div>
        </div>
      </nav>
      {/* Main Content */}
      <main style={{ padding: "32px 0 0 0" }}>
        {currentView === "blog" && <BlogMain />}
        {currentView === "article" && currentArticle && (
          <ArticleView article={currentArticle} />
        )}
        {currentView === "admin" && <AdminPanel />}
      </main>
      {/* Footer */}
      <footer
        style={{
          background: theme.footerBackground,
          borderTop: `1px solid ${theme.border}`,
          marginTop: "64px",
          padding: "32px 0",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            color: theme.textTertiary,
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          &copy; 2024 CardioHealth Blog
        </div>
      </footer>
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            backgroundColor: theme.primary,
            color: theme.buttonText,
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease",
            zIndex: 1000,
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = theme.primaryHover)
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = theme.primary)
          }
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default BlogSystem;
